import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import ImageCropModal from './ImageCropModal';

interface Track {
  id?: string;
  title: string;
  artist: string;
  cover_url: string;
  audio_url: string;
  soundcloud_url: string;
  duration: number;
  platform: string;
}

interface TrackModalProps {
  track: Track | null;
  onClose: () => void;
  onSave: () => void;
}

export default function TrackModal({ track, onClose, onSave }: TrackModalProps) {
  const [formData, setFormData] = useState<Track>({
    title: '',
    artist: '',
    cover_url: '',
    audio_url: '',
    soundcloud_url: '',
    duration: 0,
    platform: 'soundcloud',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [cropImage, setCropImage] = useState<File | null>(null);

  const platformLabels: Record<string, { label: string; placeholder: string; icon: string }> = {
    youtube: {
      label: 'URL de YouTube',
      placeholder: 'https://youtube.com/watch?v=...',
      icon: 'ri-youtube-line',
    },
    soundcloud: {
      label: 'URL de SoundCloud',
      placeholder: 'https://soundcloud.com/...',
      icon: 'ri-soundcloud-line',
    },
    spotify: {
      label: 'URL de Spotify',
      placeholder: 'https://open.spotify.com/track/...',
      icon: 'ri-spotify-line',
    },
  };

  const currentPlatform = platformLabels[formData.platform] || platformLabels.soundcloud;

  useEffect(() => {
    if (track) {
      setFormData(track);
    }
  }, [track]);

  const uploadCover = async (blob: Blob) => {
    setUploadingCover(true);
    setError('');
    try {
      const fileName = `track_cover_${Date.now()}_${Math.random().toString(36).slice(2)}.jpg`;
      const { data, error: uploadError } = await supabase.storage
        .from('Fotos')
        .upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) {
        setError(`Error al subir la portada: ${uploadError.message}`);
        return;
      }
      if (data) {
        const { data: urlData } = supabase.storage.from('Fotos').getPublicUrl(data.path);
        setFormData(prev => ({ ...prev, cover_url: urlData.publicUrl }));
      }
    } catch {
      setError('Error inesperado al subir la portada.');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (track?.id) {
        const { error: updateError } = await supabase
          .from('tracks')
          .update({
            title: formData.title,
            artist: formData.artist || null,
            cover_url: formData.cover_url || null,
            audio_url: formData.audio_url || null,
            soundcloud_url: formData.soundcloud_url || null,
            duration: formData.duration || null,
            platform: formData.platform,
          })
          .eq('id', track.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('tracks')
          .insert([{
            title: formData.title,
            artist: formData.artist || null,
            cover_url: formData.cover_url || null,
            audio_url: formData.audio_url || null,
            soundcloud_url: formData.soundcloud_url || null,
            duration: formData.duration || null,
            platform: formData.platform,
          }]);

        if (insertError) throw insertError;
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-cyan-500/30 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gray-900 border-b border-cyan-500/30 p-6 flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">
              {track ? 'Editar Track' : 'Nuevo Track'}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Artista
              </label>
              <input
                type="text"
                value={formData.artist}
                onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
              />
            </div>

            {/* Portada con subida desde PC */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Portada del track
              </label>

              {/* Preview */}
              {formData.cover_url && (
                <div className="relative w-32 h-32 mb-3 rounded-lg overflow-hidden border border-cyan-500/30">
                  <img
                    src={formData.cover_url}
                    alt="Portada"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, cover_url: '' })}
                    className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/70 hover:bg-red-500/80 rounded-full text-white text-xs transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}

              {/* Botón subir desde PC */}
              <label className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap mb-3 ${
                uploadingCover
                  ? 'bg-gray-800 border-gray-700 text-gray-500'
                  : 'bg-cyan-500/20 hover:bg-cyan-500/30 border-cyan-400/50 text-cyan-400'
              }`}>
                <i className={uploadingCover ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                {uploadingCover ? 'Subiendo...' : 'Subir desde PC'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingCover}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setCropImage(f);
                    e.target.value = '';
                  }}
                />
              </label>

              {/* O pegar URL */}
              <input
                type="url"
                value={formData.cover_url}
                onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="O pega aquí la URL de la portada"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                URL del audio (MP3)
              </label>
              <input
                type="url"
                value={formData.audio_url}
                onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Deja vacío si usas una plataforma externa
              </p>
            </div>

            {/* Platform selector */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Plataforma externa
              </label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors cursor-pointer"
              >
                <option value="soundcloud">SoundCloud</option>
                <option value="youtube">YouTube</option>
                <option value="spotify">Spotify</option>
              </select>
            </div>

            {/* Dynamic URL field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <i className={`${currentPlatform.icon} text-cyan-400`}></i>
                {currentPlatform.label}
              </label>
              <input
                type="url"
                value={formData.soundcloud_url}
                onChange={(e) => setFormData({ ...formData, soundcloud_url: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder={currentPlatform.placeholder}
              />
              <p className="text-xs text-gray-500 mt-1">
                El botón mostrará &quot;Escuchar en {formData.platform.charAt(0).toUpperCase() + formData.platform.slice(1)}&quot;
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Duración (segundos)
              </label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ejemplo: 3600 = 1 hora
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || uploadingCover}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de recorte */}
      {cropImage && (
        <ImageCropModal
          imageFile={cropImage}
          aspectRatio={1}
          onConfirm={(blob) => {
            uploadCover(blob);
            setCropImage(null);
          }}
          onCancel={() => {
            setCropImage(null);
          }}
          title="Recortar portada del track"
        />
      )}
    </>
  );
}
